// Implementation based on https://usehooks-ts.com/react-hook/use-scroll-lock#hook
import { useEffect, useRef, useState } from 'react';

type Options = {
  autoLock?: boolean;
  lockTarget?: HTMLElement | string | null;
  widthReflow?: boolean;
};

type OriginalStyle = {
  overflow: CSSStyleDeclaration['overflow'];
  paddingRight: CSSStyleDeclaration['paddingRight'];
};

export const useLockScroll = (options: Options) => {
  const { autoLock = true, lockTarget, widthReflow } = options;
  const [isLocked, setIsLocked] = useState(false);
  const target = useRef<HTMLElement | null>(null);
  const originalStyles = useRef<OriginalStyle | null>(null);

  const lock = () => {
    if (target.current) {
      const { overflow, paddingRight } = target.current.style;

      originalStyles.current = { overflow, paddingRight };

      if (widthReflow) {
        // Use window inner width if body is the target as global scrollbar isn't part of the document
        const offsetWidth =
          target.current === document.body ? window.innerWidth : target.current.offsetWidth;
        // Get current computed padding right in pixels
        const currentPaddingRight =
          parseInt(window.getComputedStyle(target.current).paddingRight, 10) || 0;

        const scrollbarWidth = offsetWidth - target.current.scrollWidth;
        target.current.style.paddingRight = `${scrollbarWidth + currentPaddingRight}px`;
      }

      target.current.style.overflow = 'hidden';

      setIsLocked(true);
    }
  };

  const unlock = () => {
    if (target.current && originalStyles.current) {
      target.current.style.overflow = originalStyles.current.overflow;

      if (widthReflow) {
        target.current.style.paddingRight = originalStyles.current.paddingRight;
      }
    }

    setIsLocked(false);
  };

  useEffect(() => {
    if (lockTarget) {
      target.current =
        typeof lockTarget === 'string' ? document.querySelector(lockTarget) : lockTarget;
    }

    if (!target.current) {
      target.current = document.body;
    }

    if (autoLock) {
      lock();
    }

    return () => {
      unlock();
    };
  }, [autoLock, lockTarget]);

  return { isLocked, lock, unlock };
};
